import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  HostBinding,
  Inject,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  AIStore,
  IQBoostViewStore,
  IqBoostPrompt,
  LANGFUSE_SYSTEM_TAG,
  SYSTEM_PROMPT_SUGGESTED_PROMPTS,
  LangfuseResponseExtensionSuggestedPrompt,
  MaximizerAiService,
  SystemType,
  ViewType,
  LanguageHelper,
  AiFeaturesService,
} from '@maximizer/ai/data-access';
import { CDN_URL } from '@maximizer/core/shared/domain';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { hyphenatedToSentence, isNullOrUndefinedOrEmpty } from '@maximizer/shared/util';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import {
  ComboBoxComponent,
  KENDO_COMBOBOX,
} from '@progress/kendo-angular-dropdowns';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { catchError, map, Observable, of, tap } from 'rxjs';
import {
  EventAction,
  EventJourney,
  IQBoostEventTracker,
} from '../classes/event-tracker';
import { Helper, IQBoostTextVersion, StringOrUndefined } from '../classes/iqboost';
import { IqboostRecentUserPrompts } from '../classes/prompts/iqboost-recent-user-prompts';
import { FeedbackComponent } from '../feedback/feedback.component';
import { ClickableRefPipe } from '../pipes/clickable-ref.pipe';
import { CreateCRMObjectPipe } from '../pipes/create-crm-object.pipe';
import { DeAnonymizerPipe } from '../pipes/de-anonymizer.pipe';
import { EntityKeyRefPipe } from '../pipes/entity-key-ref.pipe';
import { MarkdownRenderingPipe } from '../pipes/markdown-rendering.pipe';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';
import { WebSearchRefPipe } from '../pipes/web-search-ref.pipe';
import { questionTextValidator } from '../validators/question-text.validator';
import { FeatureService } from '@maximizer/core/shared/data-access';

interface GroupedPromptItem extends LangfuseResponseExtensionSuggestedPrompt {
  category: string;
}

@Component({
  selector: 'ai-iqboost-summary',
  templateUrl: './iqboost-summary.component.html',
  styles: [],
  imports: [
    ClickableRefPipe,
    CreateCRMObjectPipe,
    DeAnonymizerPipe,
    EntityKeyRefPipe,
    FeedbackComponent,
    KENDO_BUTTON,
    KENDO_COMBOBOX,
    MarkdownRenderingPipe,
    NgClass,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SafeHtmlPipe,
    WebSearchRefPipe,
    TranslatePipe,
    UI_PIPES,
    UI_KENDO,
  ],
})
export class IqboostSummaryComponent
  extends DisposableComponent
  implements AfterViewInit
{
  @ViewChild('feedback') feedback?: FeedbackComponent;

  hyphenatedToSentence = hyphenatedToSentence;
  ViewType = ViewType;
  

  @HostBinding('class') hostClasses = 'ai-ng';
  aiClass = true;

  private readonly suggestedSystemPrompts = signal([] as GroupedPromptItem[]);
  private readonly recentQuestionsText = signal('');
  private readonly questionsToHelpYouGetStartedText = signal('');
  private readonly userPrompts = computed(() =>
    this.recentUserPrompts.getRecentUserPrompts()
      .map((prompt) => ({ text: prompt, category: this.recentQuestionsText() } as GroupedPromptItem))
  );

  readonly questionControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [questionTextValidator],
  });
  readonly isAllHouseholdsEnabled = signal(false);
  readonly earlyAccessText = signal('');
  readonly promptsMenu = signal({ collapsed: false});
  readonly groupCollapse = signal<Record<string, boolean>>({});

  readonly isSLE = computed(() => this.aiFeatures.isSLE());
  readonly textVersion = computed(() => this.isSLE() 
    ? IQBoostTextVersion.defaultVersion 
    : IQBoostTextVersion.faVersion
  );
  readonly remainingCredits = computed(() => {
    if (this.aiStore.isEarlyAccess()) {
      return this.earlyAccessText();
    }

    const total = this.aiStore.aiUserDataGroup().admin.creditsTotal;
    const used = this.aiStore.aiUserDataGroup().admin.creditsUsed;
    return Math.max(0, total - used);
  });
  readonly allPromptsGrouped = computed(() => {
    return groupBy([
      ...this.userPrompts(),
      ...this.suggestedSystemPrompts(),
    ], [
      { field: "category" },
    ]) as GroupResult[];
  });

  constructor(
    public readonly store: IQBoostViewStore,
    public readonly aiStore: AIStore,
    public readonly tracker: IQBoostEventTracker,
    private readonly feature: FeatureService,
    private readonly aiFeatures: AiFeaturesService,
    private readonly translate: TranslateService,
    private readonly maximizerAi: MaximizerAiService,
    private readonly recentUserPrompts: IqboostRecentUserPrompts,
    @Inject(CDN_URL) readonly cdn: string,
  ) { 
    super();

    this.isAllHouseholdsEnabled.set(this.feature.isFeatureOn('core-ai-iq-boost-menu-shown', false));
    this.earlyAccessText.set(this.translate.instant('ai.earlyAccess'));
    this.recentQuestionsText.set(this.translate.instant('iqboost.suggestPrompt.fromCombo.recentQuestions'));
    this.questionsToHelpYouGetStartedText.set(this.translate.instant('iqboost.suggestPrompt.fromCombo.questionsToHelpYouGetStarted'));

    this.addSubscription(
      this.getSuggestedPrompts()
        .pipe(
          tap((prompts) => this.setSuggestedPrompts(prompts)),
        )
        .subscribe(),
    );
  }

  private getSuggestedPrompts(): Observable<
    LangfuseResponseExtensionSuggestedPrompt[]
  > {
    return this.maximizerAi
      .lookupPrompts({
        project: this.aiFeatures.isSLE() ? 'IqBoostSLE' : 'IqBoost',
        filter: { tag: [LANGFUSE_SYSTEM_TAG], name: [SYSTEM_PROMPT_SUGGESTED_PROMPTS] },
      })
      .pipe(
        map(
          (prompts) =>
            prompts.find(
              (prompt) =>
                prompt.config?.maximizer?.systemType ===
                SystemType.SUGGESTED_PROMPTS,
            )?.config?.maximizer?.suggestedPrompts ?? [],
        ),
        // Starter questions are optional; a failure must not kill the subscription.
        catchError(() => of([] as LangfuseResponseExtensionSuggestedPrompt[])),
      );
  }

  ngAfterViewInit(): void {
    if (this.feedback) {
      this.feedback.disabled.set(
        this.store.getFeedbackDisabled(this.store.aiResponse().summary ?? '')
      );
    }
  }

  isSelected(menuPrompt: IqBoostPrompt, selectedPrompt?: IqBoostPrompt): boolean {
    return menuPrompt.name === (selectedPrompt?.name ?? '');
  }

  usePrompt(prompt: IqBoostPrompt, useAllHouseholds = false): void {
    Helper.usePrompt(this.store, prompt, useAllHouseholds);
    this.feedback?.disabled.set(false);
  }

  regenerateReport(): void {
    this.store.setAiResponse('', ViewType.Report);
    this.store.setLoading(true);
    this.store.setViewType(ViewType.Report);
    this.store.triggerAskAiEvent({ invalidateCache: true });
    this.tracker.trackEvent(
      EventJourney.Prompt, 
      EventAction.Clicked, 
      {
        name: 'regenerate-report',
        prompt: this.store.currentPrompt()?.name ?? '',
      });
    this.feedback?.disabled.set(false);
  }

  generateViewDetailedReport(): void {
    // The report view makes its own generate-response call once it is created.
    this.store.setLoading(true);
    this.store.setViewType(ViewType.DetailedReport);
    this.tracker.trackEvent(
      EventJourney.Prompt, 
      EventAction.Clicked, 
      {
        name: 'generate-detailed-report',
        prompt: this.store.currentPrompt()?.name ?? '',
      });
  }

  submitQuestionFromControl(combo?: ComboBoxComponent): void {
    if (this.store.isBusy()) {
      return;
    }

    Helper.updateAndMarkInputControl(this.questionControl);

    const success = this.gotoMakeQuestion(this.questionControl.value);
    if (success) {
      combo?.toggle(false);
    }
  }

  onComboInput(event: Event, combo: ComboBoxComponent): void {
    const target = event.target as HTMLInputElement | null;
    combo.toggle(isNullOrUndefinedOrEmpty(target?.value));
  }

  saveNote(): void {
    const message = this.translate.instant('iqboost.summary.createNoteConfirmation');
    this.store.triggerShowConfirmationDialog(message);
  }

  toggleMenuSidebar(): void {
    this.store.setSidebarMenuCollapsed(!this.store.sidebarMenuCollapsed())
  }

  toggleSubMenu(): void {
    const value = this.promptsMenu();
    value.collapsed = !value.collapsed;
    this.promptsMenu.set(value);
  }

  isGroupCollapsed(key: string): boolean {
    return this.groupCollapse()[key] ?? false;
  }

  toggleGroup(key: string): void {
    const map = { ...this.groupCollapse() };
    map[key] = !this.isGroupCollapsed(key);
    this.groupCollapse.set(map);
  }

  getPromptName(prompt?: IqBoostPrompt): string {
    return prompt?.title ?? '';
  }

  private setSuggestedPrompts(prompts: LangfuseResponseExtensionSuggestedPrompt[]): void {
    this.suggestedSystemPrompts.set(
      prompts
        .filter(prompt => !prompt.type || (prompt.type as StringOrUndefined)?.toLowerCase() === LanguageHelper.language())
        .map(prompt => ({
          text: prompt.text,
          category: this.questionsToHelpYouGetStartedText(),
        } as GroupedPromptItem))
    );
  }

  private gotoMakeQuestion(text?: string): boolean {
    const normalizedText = Helper.normalizeQuestionText(text);
    if (!normalizedText) {
      return false;
    }

    this.store.setOutsideQuestion(normalizedText);
    this.recentUserPrompts.pushUserPrompt(normalizedText);
    this.store.setViewType(ViewType.Chat);
    this.tracker.trackEvent(
      EventJourney.AskQuestion, 
      EventAction.Started,
      {
        prompt: this.store.currentPrompt()?.name ?? '',
      });
    Helper.resetInputControl(this.questionControl);
    return true;
  }
}
